import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import { DocumentLinkTargetKind, DocumentType } from "@xynex-money/shared";
import { api, ApiError, fetchBlobUrl, request } from "../api";
import { Modal } from "../components/Modal";
import { fmtDate } from "../fmt";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { cameraAvailable, captureImageAsFile, filesToFileList } from "../native/camera";

const DOC_TYPES = DocumentType.options;

const TARGET_KINDS = DocumentLinkTargetKind.options;
type TargetKind = DocumentLinkTargetKind;

const TARGET_ENDPOINT: Record<TargetKind, string> = {
  party: "/api/parties",
  account: "/api/accounts",
  asset: "/api/assets",
  loan: "/api/loans",
  transaction: "/api/transactions",
  income: "/api/incomes",
};

interface DocumentRow {
  id: string;
  fy: string | null;
  docType: string;
  title: string;
  mime: string | null;
  sizeBytes: number;
  source: string;
  ocrStatus: string;
  createdAt: string;
}

function targetLabel(kind: TargetKind, row: Record<string, unknown>): string {
  switch (kind) {
    case "party":
      return String(row.name);
    case "account":
      return String(row.name);
    case "asset":
      return String(row.name);
    case "loan":
      return String(row.lender);
    case "transaction":
      return `${row.date} ${row.narration ?? ""} ${row.amount}`;
    case "income":
      return `${row.periodMonth} ${row.kind}`;
    default:
      return String(row.id);
  }
}

export function Vault() {
  const [docs, setDocs] = useState<DocumentRow[]>([]);
  const [fy, setFy] = useState("");
  const [docType, setDocType] = useState("");
  const [q, setQ] = useState("");
  const [error, setError] = useState<string | null>(null);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [uploadFiles, setUploadFiles] = useState<FileList | null>(null);
  const [uploadDocType, setUploadDocType] = useState<DocumentType>("other");
  const [uploadFy, setUploadFy] = useState("");
  const [uploadTitle, setUploadTitle] = useState("");
  const [busy, setBusy] = useState(false);

  const [preview, setPreview] = useState<{ doc: DocumentRow; url: string } | null>(null);

  const [linkDoc, setLinkDoc] = useState<DocumentRow | null>(null);
  const [linkKind, setLinkKind] = useState<TargetKind>("party");
  const [linkTargets, setLinkTargets] = useState<Record<string, unknown>[]>([]);
  const [linkTargetId, setLinkTargetId] = useState("");

  // Debounced so free-text search doesn't fire a request per keystroke; fy/
  // docType are discrete selects so they stay un-debounced (immediate).
  const debouncedQ = useDebouncedValue(q, 300);

  const load = () => {
    const qs = new URLSearchParams();
    if (fy) qs.set("fy", fy);
    if (docType) qs.set("doc_type", docType);
    if (debouncedQ) qs.set("q", debouncedQ);
    const suffix = qs.toString() ? `?${qs}` : "";
    api
      .get<DocumentRow[]>(`/api/documents${suffix}`)
      .then(setDocs)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load documents"));
  };

  useEffect(load, [fy, docType, debouncedQ]);

  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview.url);
    };
  }, [preview]);

  const onUpload = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!uploadFiles || uploadFiles.length === 0) {
      setError("choose at least one file");
      return;
    }
    setBusy(true);
    try {
      const fd = new FormData();
      for (const f of Array.from(uploadFiles)) fd.append("files", f);
      fd.append("doc_type", uploadDocType);
      if (uploadFy) fd.append("fy", uploadFy);
      if (uploadTitle && uploadFiles.length === 1) fd.append("title", uploadTitle);
      await request("/api/documents", { method: "POST", body: fd });
      setUploadOpen(false);
      setUploadFiles(null);
      setUploadTitle("");
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "upload failed");
    } finally {
      setBusy(false);
    }
  };

  const onPreview = async (doc: DocumentRow) => {
    setError(null);
    try {
      const { url } = await fetchBlobUrl(`/api/documents/${doc.id}/file`);
      setPreview({ doc, url });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "could not load preview");
    }
  };

  const onDelete = async (doc: DocumentRow) => {
    if (!confirm(`Delete "${doc.title}"?`)) return;
    try {
      await api.delete(`/api/documents/${doc.id}`);
      if (preview?.doc.id === doc.id) setPreview(null);
      load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "delete failed");
    }
  };

  const openLink = (doc: DocumentRow) => {
    setLinkDoc(doc);
    setLinkKind("party");
    setLinkTargetId("");
  };

  useEffect(() => {
    if (!linkDoc) return;
    api
      .get<Record<string, unknown>[]>(TARGET_ENDPOINT[linkKind])
      .then(setLinkTargets)
      .catch((err) => setError(err instanceof ApiError ? err.message : "failed to load link targets"));
  }, [linkDoc, linkKind]);

  const onLink = async () => {
    if (!linkDoc || !linkTargetId) return;
    try {
      await api.post(`/api/documents/${linkDoc.id}/links`, {
        target_kind: linkKind,
        target_id: linkTargetId,
      });
      setLinkDoc(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "link failed");
    }
  };

  const isInline = (mime: string | null) =>
    !!mime && (mime === "application/pdf" || mime.startsWith("image/"));

  return (
    <div>
      <h1>Document Vault</h1>
      {error && <div className="error-text">{error}</div>}

      <div className="toolbar">
        <div className="field">
          <label htmlFor="v-fy">FY</label>
          <input id="v-fy" placeholder="FY 2024-25" value={fy} onChange={(e) => setFy(e.target.value)} />
        </div>
        <div className="field">
          <label htmlFor="v-type">Doc type</label>
          <select id="v-type" value={docType} onChange={(e) => setDocType(e.target.value)}>
            <option value="">All</option>
            {DOC_TYPES.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
        </div>
        <div className="field">
          <label htmlFor="v-q">Search</label>
          <input id="v-q" placeholder="title or contents" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button className="primary" type="button" onClick={() => setUploadOpen(true)}>
          Upload
        </button>
      </div>

      <div style={{ display: "grid", gridTemplateColumns: preview ? "1fr 1fr" : "1fr", gap: "1rem" }}>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>Title</th>
                <th>Type</th>
                <th>FY</th>
                <th>Uploaded</th>
                <th>OCR</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {docs.map((d) => (
                <tr key={d.id} style={{ cursor: "pointer" }}>
                  <td onClick={() => onPreview(d)}>{d.title}</td>
                  <td onClick={() => onPreview(d)}>{d.docType}</td>
                  <td onClick={() => onPreview(d)}>{d.fy ?? "-"}</td>
                  <td onClick={() => onPreview(d)}>{fmtDate(d.createdAt)}</td>
                  <td onClick={() => onPreview(d)}>{d.ocrStatus}</td>
                  <td>
                    <span className="actions-inline">
                      <button type="button" onClick={() => openLink(d)}>
                        Link
                      </button>
                      <button type="button" className="danger" onClick={() => onDelete(d)}>
                        Delete
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
              {docs.length === 0 && (
                <tr>
                  <td colSpan={6} className="empty-state">
                    No documents match these filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {preview && (
          <div className="card">
            <div className="modal-header">
              <strong>{preview.doc.title}</strong>
              <button type="button" onClick={() => setPreview(null)}>
                Close
              </button>
            </div>
            {isInline(preview.doc.mime) ? (
              <iframe title="preview" src={preview.url} style={{ width: "100%", height: "70vh", border: "none" }} />
            ) : (
              <div className="muted">
                No inline preview for {preview.doc.mime ?? "this file type"}.{" "}
                <a href={preview.url} download={preview.doc.title}>
                  Download instead
                </a>
              </div>
            )}
          </div>
        )}
      </div>

      {uploadOpen && (
        <Modal title="Upload documents" onClose={() => setUploadOpen(false)}>
          <form onSubmit={onUpload} className="modal-body">
            <div className="field">
              <label htmlFor="u-files">Files (or a .zip of files)</label>
              <input
                id="u-files"
                type="file"
                multiple
                onChange={(e) => setUploadFiles(e.target.files)}
              />
              {cameraAvailable() && (
                <button
                  type="button"
                  style={{ marginTop: "0.5rem" }}
                  onClick={async () => {
                    try {
                      const file = await captureImageAsFile("scan.jpg");
                      setUploadFiles(filesToFileList([file]));
                    } catch {
                      /* user cancelled the camera */
                    }
                  }}
                >
                  Capture (camera)
                </button>
              )}
            </div>
            <div className="field">
              <label htmlFor="u-type">Doc type</label>
              <select
                id="u-type"
                value={uploadDocType}
                onChange={(e) => setUploadDocType(e.target.value as DocumentType)}
              >
                {DOC_TYPES.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="u-fy">FY (optional)</label>
              <input id="u-fy" placeholder="FY 2024-25" value={uploadFy} onChange={(e) => setUploadFy(e.target.value)} />
            </div>
            {uploadFiles && uploadFiles.length === 1 && (
              <div className="field">
                <label htmlFor="u-title">Title override (optional)</label>
                <input id="u-title" value={uploadTitle} onChange={(e) => setUploadTitle(e.target.value)} />
              </div>
            )}
            <div className="modal-actions">
              <button type="button" onClick={() => setUploadOpen(false)}>
                Cancel
              </button>
              <button className="primary" type="submit" disabled={busy}>
                {busy ? "Uploading..." : "Upload"}
              </button>
            </div>
          </form>
        </Modal>
      )}

      {linkDoc && (
        <Modal title={`Link "${linkDoc.title}"`} onClose={() => setLinkDoc(null)}>
          <div className="modal-body">
            <div className="field">
              <label htmlFor="l-kind">Link to</label>
              <select
                id="l-kind"
                value={linkKind}
                onChange={(e) => {
                  setLinkKind(e.target.value as TargetKind);
                  setLinkTargetId("");
                }}
              >
                {TARGET_KINDS.map((k) => (
                  <option key={k} value={k}>
                    {k}
                  </option>
                ))}
              </select>
            </div>
            <div className="field">
              <label htmlFor="l-target">{linkKind}</label>
              <select id="l-target" value={linkTargetId} onChange={(e) => setLinkTargetId(e.target.value)}>
                <option value="">-- select --</option>
                {linkTargets.map((t) => (
                  <option key={String(t.id)} value={String(t.id)}>
                    {targetLabel(linkKind, t)}
                  </option>
                ))}
              </select>
            </div>
            <div className="modal-actions">
              <button type="button" onClick={() => setLinkDoc(null)}>
                Cancel
              </button>
              <button className="primary" type="button" disabled={!linkTargetId} onClick={onLink}>
                Link
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
